"use client";

import { useDeferredValue, useState } from "react";

import type { StoryRecord, TagDefinition } from "../../_types/content";
import StoryLinkCard from "./StoryLinkCard";
import CatalogTemplate from "./ui/CatalogTemplate";
import { FilterDropdown, getGroupedTagOptions } from "./ui/GroupedTagFilters";
import { Select } from "./ui/Primitives";

type StorySort = "featured" | "newest" | "title";

export interface StoryCatalogFilters {
  locations: string[];
  tagIdsByType: Record<string, string[]>;
  sort: StorySort;
}

const defaultFilters: StoryCatalogFilters = {
  locations: [],
  tagIdsByType: {},
  sort: "featured",
};

function getStoryLocation({ locationLabel, user }: StoryRecord) {
  return locationLabel ?? user.profile.location.region;
}

export function getStoryFilterOptions(
  records: readonly StoryRecord[],
  tags: readonly TagDefinition[],
) {
  const storyTagIds = new Set(records.flatMap(({ story }) => story.tagIds));
  const tagGroups = getGroupedTagOptions(storyTagIds, tags);
  const locations = [
    ...new Set(records.map((record) => getStoryLocation(record))),
  ].toSorted();

  return { locations, tagGroups };
}

export function filterAndSortStories(
  records: readonly StoryRecord[],
  filters: StoryCatalogFilters,
) {
  return [...records]
    .filter((record) => {
      const matchesLocation =
        filters.locations.length === 0 ||
        filters.locations.includes(getStoryLocation(record));
      const matchesTags = Object.values(filters.tagIdsByType).every(
        (selectedTagIds) =>
          selectedTagIds.length === 0 ||
          selectedTagIds.some((tagId) => record.story.tagIds.includes(tagId)),
      );

      return matchesLocation && matchesTags;
    })
    .toSorted((first, second) => {
      switch (filters.sort) {
        case "newest":
          return (
            new Date(second.story.publishedAt ?? 0).getTime() -
            new Date(first.story.publishedAt ?? 0).getTime()
          );
        case "title":
          return first.story.headline.localeCompare(second.story.headline);
        default:
          if (first.story.featured !== second.story.featured) {
            return first.story.featured ? -1 : 1;
          }

          return (
            (first.story.featuredOrder ?? Number.MAX_SAFE_INTEGER) -
            (second.story.featuredOrder ?? Number.MAX_SAFE_INTEGER)
          );
      }
    });
}

function StoryFilterControls({
  filters,
  locations,
  tagGroups,
  onToggleLocation,
  onToggleTag,
  onSortChange,
  onReset,
}: {
  filters: StoryCatalogFilters;
  locations: string[];
  tagGroups: ReturnType<typeof getStoryFilterOptions>["tagGroups"];
  onToggleLocation: (location: string) => void;
  onToggleTag: (type: string, tagId: string) => void;
  onSortChange: (sort: StorySort) => void;
  onReset: () => void;
}) {
  return (
    <div className="space-y-7">
      <FilterDropdown
        label="Region"
        options={locations.map((location) => ({
          value: location,
          label: location,
        }))}
        selectedValues={filters.locations}
        onToggle={onToggleLocation}
      />
      {tagGroups.map((group) => (
        <FilterDropdown
          key={group.type}
          label={group.label}
          options={group.options.map((tag) => ({
            value: tag.id,
            label: tag.label,
          }))}
          selectedValues={filters.tagIdsByType[group.type] ?? []}
          onToggle={(tagId) => onToggleTag(group.type, tagId)}
        />
      ))}

      <div className="border-t border-burgundy/20" aria-hidden="true" />

      <label className="block font-[700]">
        Sort by
        <Select
          value={filters.sort}
          onChange={(event) => onSortChange(event.target.value as StorySort)}
        >
          <option value="featured">Featured</option>
          <option value="newest">Newest</option>
          <option value="title">Title: A-Z</option>
        </Select>
      </label>

      <button
        type="button"
        onClick={onReset}
        className="border-b-2 border-burgundy font-[700]"
      >
        Reset filters
      </button>
    </div>
  );
}

export default function StoryCatalog({
  records,
  tags,
}: {
  records: readonly StoryRecord[];
  tags: readonly TagDefinition[];
}) {
  const [filters, setFilters] = useState(defaultFilters);
  const deferredFilters = useDeferredValue(filters);
  const filteredRecords = filterAndSortStories(records, deferredFilters);
  const { locations, tagGroups } = getStoryFilterOptions(records, tags);

  function toggleLocation(location: string) {
    setFilters((current) => ({
      ...current,
      locations: current.locations.includes(location)
        ? current.locations.filter((value) => value !== location)
        : [...current.locations, location],
    }));
  }

  function toggleTag(type: string, tagId: string) {
    setFilters((current) => {
      const selectedTagIds = current.tagIdsByType[type] ?? [];
      return {
        ...current,
        tagIdsByType: {
          ...current.tagIdsByType,
          [type]: selectedTagIds.includes(tagId)
            ? selectedTagIds.filter((value) => value !== tagId)
            : [...selectedTagIds, tagId],
        },
      };
    });
  }

  return (
    <CatalogTemplate
      filters={
        <StoryFilterControls
          filters={filters}
          locations={locations}
          tagGroups={tagGroups}
          onToggleLocation={toggleLocation}
          onToggleTag={toggleTag}
          onSortChange={(sort) => setFilters({ ...filters, sort })}
          onReset={() => setFilters(defaultFilters)}
        />
      }
      count={filteredRecords.length}
      countLabel="stories"
      isEmpty={filteredRecords.length === 0}
      emptyMessage="No stories match these filters."
      onReset={() => setFilters(defaultFilters)}
    >
      {filteredRecords.map((record, index) => (
        <StoryLinkCard key={record.story.id} {...record} index={index} />
      ))}
    </CatalogTemplate>
  );
}
